import { memo, Fragment, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import classNames from 'classnames';
import Collapse from 'react-collapse';
import OrderStatus from './OrderStatus/OrderStatus';
import Segment from '../../../components/Segment';
import OrderTimer from './OrderTimer/OrderTimer';
import OrderSegment from './OrderSegment/OrderSegment';
import Icon from '../../../directives/Icon/Icon';
import Revoke from '../../../directives/Revoke/Revoke';
import { orderToken } from '../../routers';
import Price from '../../../directives/Price/Price';
import Button from '../../../ui/Button';
import { media } from '../../../ui';
import { BOOKED, CANCEL, PAID, ON_WAY, SUCCESS, RETURNED, CASH_BOOKING, getHistoryStatus } from './helper';
import OrderInsuranceContainer from './OrderInsurance/OrderInsuranceContainer';
import { createSameTicketClickEvent } from '../../../../helpers/data-layer-helper';
import { save as localStorageSave } from '../../../../helpers/local-storage-serializer';
import { SAME_TICKET_SEARCH_WITH_CHILD } from '../../../../constants/passengers';
import { getReturnableReasons } from '../../../../helpers/account-helper';
import domainVariants from '../../../../global-variables-provider/domain-variants';
const SegmentWrapper = styled.div.withConfig({
  displayName: "Order__SegmentWrapper",
  componentId: "sc-1avahp3-0"
})(["padding:18px 25px 40px;.order-details__segment{margin-bottom:16px;margin-left:-25px;", "}"], media.xs`
      margin-bottom: 10px;
      margin-right: -25px;
      margin-left: -25px;
    `);
const Order = ({
  orderId,
  cityFrom,
  cityTo,
  datetimeAction,
  status,
  price,
  actions,
  segments,
  payPath,
  searchPath,
  downloadTicketsPath,
  downloadInsurancesPath,
  isDeleted,
  isReturnable,
  isAuthorizedToRefund,
  isFeedback,
  insurances,
  isButtonBuyShown,
  isButtonDownloadShown,
  isButtonCommentShown,
  isButtonSearchShown,
  isFeedbackCompleted,
  isRatingEnabled,
  isExtended,
  toggleSegmentDetails,
  transferTime,
  isBlaBlaCar,
  routes,
  hasChild,
  isHistoryTrip,
  isCashBooking,
  errorText,
  linkToSupport
}) => {
  const [isCashBookingRefundLoading, setIsLoading] = useState(false);
  const sameTicketButtonHandler = useCallback(() => {
    if (hasChild) {
      localStorageSave(SAME_TICKET_SEARCH_WITH_CHILD, 1);
    }
    createSameTicketClickEvent();
  }, [hasChild]);
  if (isDeleted) {
    return /*#__PURE__*/React.createElement(Revoke, {
      onClick: actions.restore
    }, I18n.t('components.Order.revoke'));
  }
  const isPrimary = status === BOOKED;
  const isDisabled = status === CANCEL;
  const isOnWay = status === ON_WAY;
  const isHistoryStatus = getHistoryStatus(status);
  const cls = classNames('new-account__order', {
    primary: isPrimary,
    'new-account__order--disabled': isDisabled
  });
  const hasManySegments = segments.length > 1;
  const returnableReasons = getReturnableReasons(segments);
  const isInaccessibleForWlBr = domainVariants.isWlBr && !returnableReasons.includes('voluntary');
  const isAccessibleForWlBr = !isInaccessibleForWlBr;
  const route = `${cityFrom} — ${cityTo}`;
  const sameTicketButtonTitle = isHistoryTrip || isOnWay ? I18n.t('components.Order.button_search_same_ticket') : I18n.t('components.Order.button_repeat_order');
  const onClickCashBookingRefund = () => {
    // after api need reload page
    setIsLoading(true);
    actions.cashBookingRefund();
  };
  useEffect(() => {
    if (errorText) setIsLoading(false);
  }, [errorText]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: cls
  }, /*#__PURE__*/React.createElement("div", {
    className: "order-info"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "order-info__header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "direction"
  }, route), /*#__PURE__*/React.createElement("div", {
    className: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: "status__item"
  }, /*#__PURE__*/React.createElement(OrderStatus, {
    status: status
  })), !isHistoryStatus && /*#__PURE__*/React.createElement(OrderTimer, {
    text: status === BOOKED ? I18n.t('components.Order.booking_ends') : I18n.t('components.Order.departure'),
    datetimeAction: datetimeAction
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "status__item"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Icon, {
    name: "ticket",
    color: "error"
  })), /*#__PURE__*/React.createElement("div", null, "№", ' ', orderId))))), /*#__PURE__*/React.createElement("div", {
    className: "order-info__footer"
  }))), !isHistoryStatus && /*#__PURE__*/React.createElement("div", {
    className: classNames('order-segment')
  }, segments.map(segment => /*#__PURE__*/React.createElement(OrderSegment, {
    key: segment.segmentId,
    segment: segment
  }))), status === BOOKED ? /*#__PURE__*/React.createElement("div", {
    className: "order-actions"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Price, {
    className: "price text-nowrap",
    price: price
  })), /*#__PURE__*/React.createElement("div", null, isButtonBuyShown && /*#__PURE__*/React.createElement("a", {
    className: "btn-again",
    href: payPath
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "sm",
    title: I18n.t('components.Order.button_buy')
  })))) : /*#__PURE__*/React.createElement("div", {
    className: classNames('order-actions', {
      'order-actions--triple': [PAID, ON_WAY, SUCCESS].includes(status)
    })
  }, [PAID, ON_WAY, SUCCESS].includes(status) && (!isAuthorizedToRefund || isInaccessibleForWlBr) && /*#__PURE__*/React.createElement("div", {
    className: "order-actions--unauthorized-return"
  }, I18n.t('components.Order.unauthorized_to_return'), linkToSupport && /*#__PURE__*/React.createElement("a", {
    href: linkToSupport
  }, " link")), [PAID, ON_WAY, SUCCESS].includes(status) && isReturnable && isAuthorizedToRefund && isAccessibleForWlBr && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Link, {
    className: "btn btn-primary btn-link btn-return with-icon btn-square",
    to: routes.order.return.to(orderId, orderToken)
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    name: "arrw"
  })), /*#__PURE__*/React.createElement("span", null, I18n.t('components.Order.button_return')))), [CASH_BOOKING].includes(status) && isCashBooking && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classNames('btn btn-primary btn-link btn-return with-icon btn-square', {
      loading: isCashBookingRefundLoading
    }),
    onClick: onClickCashBookingRefund
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    name: "arrw"
  })), /*#__PURE__*/React.createElement("span", null, I18n.t('components.Order.button_cancel_cash_booking')))), [PAID, ON_WAY, CASH_BOOKING].includes(status) && isButtonDownloadShown && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
    className: "btn btn-primary btn-link btn-return with-icon btn-square",
    href: downloadTicketsPath,
    target: "_blank",
    rel: "noopener noreferrer"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    name: "downloads"
  })), /*#__PURE__*/React.createElement("span", null, I18n.t('components.Order.button_download')))), [ON_WAY, SUCCESS].includes(status) && isFeedback && isButtonCommentShown && !isFeedbackCompleted && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Link, {
    className: "btn btn-primary btn-link btn-return with-icon btn-square",
    to: routes.order.comment.to(orderId)
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    name: "bubble-simple-line-icons"
  })), /*#__PURE__*/React.createElement("span", null, I18n.t('components.Order.button_comment')))), isBlaBlaCar && [RETURNED].includes(status) && /*#__PURE__*/React.createElement("a", {
    className: "btn-again",
    href: "/"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "sm",
    title: I18n.t('components.Order.button_repeat_trip')
  })), isButtonSearchShown && !isCashBooking && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
    className: "btn-again",
    href: searchPath
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "sm",
    onClick: sameTicketButtonHandler,
    title: sameTicketButtonTitle
  })))), /*#__PURE__*/React.createElement("div", {
    className: classNames('order-toggle', {
      active: isExtended
    }),
    onClick: toggleSegmentDetails
  }, isExtended ? I18n.t('pages.search.trip.controls.collapse') : I18n.t('pages.search.trip.controls.expand')), /*#__PURE__*/React.createElement(Collapse, {
    isOpened: isExtended
  }, /*#__PURE__*/React.createElement(SegmentWrapper, {
    className: "order-details"
  }, segments.map((segment, index) => /*#__PURE__*/React.createElement(Fragment, {
    key: segment.segmentId
  }, isHistoryStatus && /*#__PURE__*/React.createElement("div", {
    className: "ogirder-details__segment"
  }, /*#__PURE__*/React.createElement(OrderSegment, {
    key: segment.segmentId,
    segment: segment
  })), /*#__PURE__*/React.createElement(Segment, {
    ...segment,
    isRatingEnabled: isRatingEnabled,
    transferTime: transferTime,
    isShowSegmentDevider: hasManySegments && index === 0,
    isIconShow: true,
    hasManySegments: hasManySegments,
    isDashed: true,
    isAccount: true
  }))))), insurances.map(insurance => /*#__PURE__*/React.createElement(OrderInsuranceContainer, {
    key: insurance.id,
    orderId: orderId,
    insurance: insurance,
    downloadPath: downloadInsurancesPath
  }))), errorText && /*#__PURE__*/React.createElement("div", {
    className: "text-danger"
  }, errorText));
};
Order.propTypes = {
  orderId: PropTypes.string,
  status: PropTypes.oneOf([BOOKED, CANCEL, PAID, ON_WAY, SUCCESS, RETURNED]),
  insurances: PropTypes.array,
  passengers: PropTypes.arrayOf(PropTypes.shape({
    passengerId: PropTypes.number
  })),
  segments: PropTypes.arrayOf(PropTypes.shape({
    segmentId: PropTypes.string
  })),
  cityFrom: PropTypes.string,
  cityTo: PropTypes.string,
  datetimeAction: PropTypes.instanceOf(Date),
  price: PropTypes.object,
  payPath: PropTypes.string,
  searchPath: PropTypes.string,
  downloadTicketsPath: PropTypes.string,
  downloadInsurancesPath: PropTypes.string,
  actions: PropTypes.shape({
    delete: PropTypes.func,
    notified: PropTypes.func,
    restore: PropTypes.func,
    cashBookingRefund: PropTypes.func
  }),
  /**
  * Флаг нажал ли пользователь кнопку удалить заказ
  * (находится ли заказ в состоянии удаления)
  */
  isDeleted: PropTypes.bool,
  /** Флаг возможности вернуть билеты по данному заказу */isReturnable: PropTypes.bool,
  /** Флаг возможности вернуть билеты онлайн как агент басфора или аффилированный агент */isAuthorizedToRefund: PropTypes.bool,
  /** Флаг возможности вернуть билеты по данному заказу */isFeedback: PropTypes.bool,
  isButtonBuyShown: PropTypes.bool,
  isButtonDownloadShown: PropTypes.bool,
  linkToSupport: PropTypes.string,
  isButtonCommentShown: PropTypes.bool,
  isButtonSearchShown: PropTypes.bool,
  isDeleteButtonShown: PropTypes.bool,
  isFeedbackCompleted: PropTypes.bool,
  isRatingEnabled: PropTypes.bool.isRequired,
  isExtended: PropTypes.bool.isRequired,
  isCashBooking: PropTypes.bool.isRequired,
  toggleSegmentDetails: PropTypes.func,
  transferTime: PropTypes.string,
  errorText: PropTypes.string,
  isBlaBlaCar: PropTypes.bool,
  isShowDownloadBtn: PropTypes.bool,
  routes: PropTypes.object,
  hasChild: PropTypes.bool,
  isHistoryTrip: PropTypes.bool
};
export default /*#__PURE__*/memo(Order);